/**
 * The UI for the persona prose rewrite enqueue endpoints.
 *
 * ⛔⛔ THIS EXISTS BECAUSE THE CAPABILITY WAS NOT DELIVERED WITHOUT IT. The endpoints shipped in
 * #4880/#4886 with NO button anywhere in admin/src. Against Jaume's standing rule of 2026-08-02 —
 * A CAPABILITY WITH NO BUTTON IS NOT DELIVERED — the rewrite was reachable only by an author with
 * a curl and a super-admin JWT. Offered the choice between "run the curls now" and "build the
 * button first", on his own critical path at 2am and four weeks into waiting, Jaume chose the
 * button. Build it as if that is what that means, because it is.
 *
 * ⇒ THE UI SURFACES THE ENDPOINT'S CONTROLS. IT DOES NOT DUPLICATE OR SOFTEN THEM.
 * Every guard below already exists server-side; the job here is to make them visible and
 * un-bypassable from the client, never to re-implement them.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';

type Endpoint = 'canonical' | 'category';

export interface PreviewState {
  /** ⛔ THE SCOPE THIS PREVIEW WAS TAKEN AT. See scopeKey — this field is the whole guard. */
  scopeKey: string;
  count: number;
  items: string[];
}

export interface WriteResult {
  enqueued: number;
  failed: number;
  skipped: number;
  failures?: Array<{ error?: string }>;
}

/**
 * Anything displayed alongside a scope must remember which scope produced it. One wrapper for
 * every such field, so a future addition inherits the property instead of re-learning it.
 */
export interface Scoped<T> {
  scopeKey: string;
  value: T;
}

/**
 * ⛔⛔ THE SCOPE KEY IS THE LOAD-BEARING PART OF THIS FILE.
 *
 * A preview is only evidence about the scope it was taken at. Preview BikeInn, switch the site
 * picker to TrekkInn, press the write button, and without this you would enqueue a corpus you
 * never previewed while the screen still shows BikeInn's count.
 *
 * 🔑 AND IT IS THE SAME DEFECT AS #4886, ONE LAYER UP. That bug was a three-valued input steering
 * a two-valued decision from two different places. "Has a preview been run" and "what will the
 * write actually target" are likewise two facts, and a boolean `hasPreviewed` flag lets them
 * disagree silently. So there is NO such flag: canWrite is DERIVED by comparing the stored key
 * against the live one, which cannot drift because it is recomputed on every render.
 */
export function scopeKey(endpoint: Endpoint, siteId: string | null): string {
  return `${endpoint}|${siteId ?? ''}`;
}

/**
 * The single derivation every write path reads. Exported so the test can pin it directly rather
 * than only through the DOM.
 */
export function canWrite(preview: PreviewState | null, currentScopeKey: string): boolean {
  if (!preview) return false;
  return preview.scopeKey === currentScopeKey;
}

/**
 * ⛔⛔ A PREVIEW YOU CANNOT PARSE IS NOT A PREVIEW OF ZERO. IT IS NOT A PREVIEW AT ALL.
 *
 * The first cut read the response as `json.wouldEnqueue ?? 0` with `json.personas || [] `. So a
 * malformed HTTP 200 — a rollout mismatch, a partial response, a field renamed — rendered
 * "Enqueue 0 rewrites", ENABLED THE WRITE, and the write then enqueued the endpoint's ENTIRE
 * scope. THE OPERATOR IS SHOWN A BLAST RADIUS OF ZERO AND FIRES THE WHOLE CORPUS. That is worse
 * than the stale-scope defect, because a stale preview at least showed a real number that a real
 * preview had produced.
 *
 * 🔑 AND IT IS THE HOLE THE SCOPE GATE STRUCTURALLY COULD NOT SEE. canWrite answers "has a preview
 * run, and was it taken at this scope" — both correctly. It never asks whether the preview is
 * TRUSTWORTHY. A gate that validates its precondition's PROVENANCE but not its CONTENT leaves the
 * evidence the whole control rests on as the one input taken on faith.
 *
 * 🔑 THIRD LAYER, ONE MECHANISM. `null -> 0` here is `preview:null -> falsy -> write` from #4886,
 * and `no siteId -> rows:[] -> "0 of 96"` from the readiness misread. A MISSING VALUE BECOMING A
 * CONFIDENT ZERO INSTEAD OF AN ERROR — three times tonight, in three different layers.
 *
 * ⇒ SO THIS FAILS CLOSED. Anything that is not an affirmatively well-formed preview surfaces an
 * error and leaves writing disabled.
 */
export function parsePreview(json: any, expectedScopeKey: string): PreviewState {
  if (!json || typeof json !== 'object') throw new Error('Preview response was not an object');
  if (json.success !== true) throw new Error(json.error || 'Preview did not report success');
  // The server echoes preview:true on the preview path. If it is absent or false, this response
  // is not a preview — and treating a WRITE response as a preview is how you double-fire.
  if (json.preview !== true) throw new Error('Response was not a preview (preview !== true)');

  const n = json.wouldEnqueue;
  // Number.isInteger rejects null, undefined, "0", "12", NaN, 1.5 and Infinity in one test.
  // A string "0" is the exact shape that would have rendered a confident, wrong zero.
  if (!Number.isInteger(n) || n < 0) {
    throw new Error(`Preview returned an unusable count (wouldEnqueue: ${JSON.stringify(n)})`);
  }

  // Exactly one of these is expected per endpoint; whichever is present must be a real array.
  const list = json.personas !== undefined ? json.personas : json.categories;
  if (!Array.isArray(list)) {
    throw new Error('Preview did not return a list of affected rows');
  }

  // ⛔⛔ THE COUNT AND THE LIST MUST AGREE, AND THIS IS A SAFETY CHECK RATHER THAN A TIDINESS ONE.
  //
  // Validating each field independently leaves them free to contradict each other:
  // `{ wouldEnqueue: 2, categories: [ …46 rows… ] }` is two individually-valid fields that
  // together describe two different operations. The button would read "Enqueue 2 rewrites" while
  // the write enqueued the full 46-row scope — A BLAST RADIUS MISREPRESENTED BEFORE THE WRITE,
  // which is the one class that still blocks once the safety controls are proven.
  //
  // 🔑 EVERY EARLIER ROUND VALIDATED FIELDS. THIS VALIDATES THE RELATIONSHIP BETWEEN THEM — the
  // failure that survives when each part is individually well-formed.
  //
  // Safe to enforce: both endpoints derive the count from the SAME array they map
  // (routes/persona-rewrite.js:220-221 `rows.length` / `rows.map`, and :355-358
  // `selected.length` / `selected.map`), so equality holds by construction server-side and this
  // cannot fire on a healthy response.
  if (list.length !== n) {
    throw new Error(
      `Preview is self-contradictory: wouldEnqueue=${n} but ${list.length} rows were listed`,
    );
  }

  // The canonical endpoint also reports the full population it selected FROM
  // (routes/persona-rewrite.js:356 `totalCanonicalRows: rows.length`, against
  // `wouldEnqueue: selected.length` where selected is a subset). A selection larger than the
  // population it came from is impossible — same class, checked only when present.
  if (Number.isInteger(json.totalCanonicalRows) && n > json.totalCanonicalRows) {
    throw new Error(
      `Preview is self-contradictory: wouldEnqueue=${n} exceeds totalCanonicalRows=${json.totalCanonicalRows}`,
    );
  }

  return { scopeKey: expectedScopeKey, count: n, items: list.map((x: unknown) => String(x)) };
}

/**
 * ⛔⛔ THE WRITE RESPONSE IS VALIDATED TOO — AND THE REASON IS DIFFERENT FROM THE PREVIEW'S.
 *
 * A malformed 200 here rendered "Enqueued undefined · skipped undefined · failed undefined".
 *
 * 🔑 THE ASYMMETRY IS WHY THIS WAS MISSED, AND IT IS WORTH STATING RATHER THAN JUST FIXING:
 * parsePreview is a SAFETY control — a bad preview must not authorise a write. This is an
 * ACCOUNTING control — the mutation HAS ALREADY HAPPENED and this is the operator's only account
 * of it. Having validated the dangerous one, the job felt done. It was not: at the checkpoint,
 * "how many were enqueued" is the number the entire run is gated on, and an `undefined` at 3am
 * cannot distinguish a full enqueue of 547 from a partial one.
 *
 * ⚠️ AND THE HONEST BOUND ON WHAT THIS BUYS: THE DATABASE IS THE RECORD, NOT THIS PANEL. Validating
 * the shape stops the UI from printing nonsense; it does not make the UI authoritative. The
 * checkpoint confirms enqueued counts from async_jobs either way.
 */
export function parseWriteResult(json: any): WriteResult {
  if (!json || typeof json !== 'object') throw new Error('Enqueue response was not an object');
  const num = (k: string) => {
    const v = json[k];
    if (!Number.isInteger(v) || v < 0) {
      throw new Error(`Enqueue response has an unusable ${k} (${JSON.stringify(v)})`);
    }
    return v as number;
  };
  const enqueued = num('enqueued');
  const failed = num('failed');
  const skipped = num('skipped');
  // `failures` is optional, but if present it must be an array — it is rendered.
  if (json.failures !== undefined && !Array.isArray(json.failures)) {
    throw new Error('Enqueue response has a malformed failures list');
  }

  /**
   * ⛔⛔ CROSS-FIELD INVARIANTS — SWEPT AS RELATIONSHIPS, NOT AS FIELDS.
   *
   * The preview's count-vs-list defect was not "another field left unvalidated", it was an
   * INVARIANT BETWEEN two fields that were each already validated. So the same question was asked
   * of this parse, and the server guarantees three such relationships by construction:
   *
   *   enqueued + skipped + failed === candidates
   *       every candidate row lands in exactly one bucket — routes/persona-rewrite.js:191-211
   *       pushes to `skipped` and `continue`s, otherwise to `created` or `failed`.
   *   failures.length === failed        (`failed: failed.length, failures: failed`)
   *   skippedRows.length === skipped    (`skipped: skipped.length, skippedRows: skipped`)
   *
   * ⚠️ EACH IS CHECKED ONLY WHEN THE FIELD IS PRESENT, AND THAT IS A DELIBERATE LIMIT RATHER THAN
   * AN OVERSIGHT. This is the ACCOUNTING control, and the stop rule for this component is that
   * accounting must not block a legitimate run. Demanding `candidates` outright would let a future
   * server that drops one optional field brick the checkpoint at 3am. Contradiction fails closed;
   * absence does not.
   */
  if (Number.isInteger(json.candidates)) {
    const sum = enqueued + skipped + failed;
    if (sum !== json.candidates) {
      throw new Error(
        `Enqueue response does not add up: ${enqueued}+${skipped}+${failed}=${sum} but candidates=${json.candidates}`,
      );
    }
  }
  if (Array.isArray(json.failures) && json.failures.length !== failed) {
    throw new Error(
      `Enqueue response is self-contradictory: failed=${failed} but ${json.failures.length} failures listed`,
    );
  }
  if (Array.isArray(json.skippedRows) && json.skippedRows.length !== skipped) {
    throw new Error(
      `Enqueue response is self-contradictory: skipped=${skipped} but ${json.skippedRows.length} skipped rows listed`,
    );
  }

  return { enqueued, failed, skipped, failures: json.failures };
}

export function PersonaRewriteEnqueue() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [preview, setPreview] = useState<Record<Endpoint, PreviewState | null>>({
    canonical: null,
    category: null,
  });
  const [busy, setBusy] = useState<Endpoint | null>(null);
  // ⛔⛔ RESULTS AND ERRORS CARRY THEIR SCOPE TOO — SWEPT AS A CLASS, NOT PATCHED AS AN INSTANCE.
  //
  // The preview was scope-keyed first and results were left behind, which is the twin pattern this
  // repo keeps producing: the fix gets written against the instance that was reported instead of
  // swept across every field with the same property. So this is the audit, stated:
  //   preview  — scoped (the original fix)
  //   result   — scoped HERE. A write fired for BikeInn whose result lands while the operator has
  //              switched to TrekkInn would otherwise print BikeInn's counts under TrekkInn's
  //              heading. THE IN-FLIGHT CASE IS THE SHARP ONE, which is why the scope is captured
  //              at FIRE TIME below and not read at render time.
  //   error    — scoped HERE TOO, and it was the third one nobody had named: a failure for site A
  //              rendered under site B's panel reads as B being broken.
  //   busy     — deliberately NOT scoped. It is a transient spinner for an in-flight request, it
  //              names no count and no site, so it cannot mislead. Scoping it would strand the
  //              control disabled after a scope change.
  const [result, setResult] = useState<Record<Endpoint, Scoped<WriteResult> | null>>({
    canonical: null,
    category: null,
  });
  const [error, setError] = useState<Record<Endpoint, Scoped<string> | null>>({
    canonical: null,
    category: null,
  });

  // ⇒ `id || _id`, matching SiteContext, which uses that form in all five of its own lookups.
  // MEASURED before treating it as load-bearing: routes/sites.js never emits _id for a site, and
  // all 43 rows in the sites table have a non-null id — BikeInn included. So this is defensive
  // compatibility with the declared Site interface, NOT a fix for a dead button tonight.
  const siteId: string | null = selectedSite?.id ?? selectedSite?._id ?? null;

  // ⛔ NOTE THERE IS NO "ALL SITES" OPTION, ANYWHERE. The endpoint permits an unscoped write when
  // an explicit limit is supplied; the UI simply does not offer that door. "Unscoped must be
  // harder to reach than scoped" is satisfied here in its strongest form — from this screen it is
  // UNREACHABLE. The category panel is inert until a site is actually selected.
  const scopes: Record<Endpoint, string> = {
    canonical: scopeKey('canonical', null),
    category: scopeKey('category', siteId),
  };

  const path = (e: Endpoint) =>
    e === 'canonical'
      ? `${API_URL}/persona-rewrite/enqueue-canonical`
      : `${API_URL}/persona-rewrite/enqueue`;

  async function runPreview(e: Endpoint) {
    // ⛔ CAPTURED AT FIRE TIME. Reading scopes[e] again in the async continuation would read the
    // scope as it is WHEN THE RESPONSE LANDS, which is exactly the bug being closed.
    const firedAt = scopes[e];
    setBusy(e);
    setError((s) => ({ ...s, [e]: null }));
    setResult((s) => ({ ...s, [e]: null }));
    try {
      // ⛔ preview:true EXPLICITLY, and confirm is never sent on this path. The flags are real
      // booleans — the route rejects anything else (#4886), and a UI that hardcoded its way past
      // that guard would recreate the very defect on the other side of the wire.
      const body: Record<string, unknown> = { preview: true };
      if (e === 'category') body.siteId = siteId;
      const res = await authFetch(path(e), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) {
        // ⛔ NO SILENT FALLBACK. The real server error is shown; we never substitute a friendly
        // default, because a preview that fails quietly reads as "nothing to do" — the exact
        // silent-zero this whole feature was built to stop reporting.
        throw new Error(json?.error || `Preview failed (HTTP ${res.status})`);
      }
      // ⇒ parsePreview THROWS rather than coercing. A 200 is not a preview; a well-formed body is.
      // ⛔ AND IT IS CALLED HERE, NOT INSIDE THE setPreview UPDATER. React runs a functional
      // updater inside its own reducer, so a throw in there escapes this try/catch entirely and
      // takes the component down instead of rendering the error. The first cut did exactly that
      // and the malformed-response tests caught it — the validation was correct and unreachable.
      const parsed = parsePreview(json, firedAt);
      setPreview((s) => ({ ...s, [e]: parsed }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError((s) => ({ ...s, [e]: { scopeKey: firedAt, value: msg } }));
      setPreview((s) => ({ ...s, [e]: null }));
    } finally {
      setBusy(null);
    }
  }

  async function runWrite(e: Endpoint) {
    // ⇒ THE GUARD IS RE-EVALUATED AT FIRE TIME, not merely reflected in `disabled`. A disabled
    // attribute is a presentation detail; this is the control.
    if (!canWrite(preview[e], scopes[e])) return;
    const firedAt = scopes[e];   // ⛔ fire time, not render time — see runPreview
    setBusy(e);
    setError((s) => ({ ...s, [e]: null }));
    try {
      const body: Record<string, unknown> = { preview: false, confirm: true };
      if (e === 'category') body.siteId = siteId;
      const res = await authFetch(path(e), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `Enqueue failed (HTTP ${res.status})`);
      // ⇒ Parsed OUTSIDE any state updater, for the reason documented in runPreview.
      const parsed = parseWriteResult(json);
      setResult((s) => ({ ...s, [e]: { scopeKey: firedAt, value: parsed } }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError((s) => ({ ...s, [e]: { scopeKey: firedAt, value: msg } }));
    } finally {
      // ⛔⛔ THE PREVIEW IS SPENT EITHER WAY, SO IT IS VOIDED IN `finally`, NOT ON THE SUCCESS PATH.
      // The request left the browser. Whether the response parsed, failed to parse, or errored,
      // the mutation may well have happened — so the preview that authorised it must not remain
      // reusable. Voiding it only on success would leave the ONE case where we are least certain
      // what happened as the one case where the button is still armed.
      setPreview((s) => ({ ...s, [e]: null }));
      setBusy(null);
    }
  }

  // A button that appears and then 403s is worse than a button that is not there.
  if (!isSuperAdmin) return null;

  const panel = (e: Endpoint, title: string, blurb: string, ready: boolean, notReady: string) => {
    const p = preview[e];
    const writable = canWrite(p, scopes[e]);
    // ⇒ A result or error is shown ONLY while the scope that produced it is still selected.
    const r = result[e]?.scopeKey === scopes[e] ? result[e]!.value : null;
    const errMsg = error[e]?.scopeKey === scopes[e] ? error[e]!.value : null;
    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{blurb}</p>

          {!ready ? (
            <p className="text-sm text-amber-600" data-testid={`${e}-not-ready`}>
              {notReady}
            </p>
          ) : (
            <>
              <Button
                variant="outline"
                disabled={busy !== null}
                onClick={() => runPreview(e)}
                data-testid={`${e}-preview`}
              >
                {busy === e ? 'Working…' : '1. Preview — writes nothing'}
              </Button>

              {/* ⛔ A STALE PREVIEW IS NOT SHOWN AS DATA. The write is already revoked by canWrite,
                  but leaving the OLD count on screen while the surrounding text names the NEW site
                  reads as "46 categories" beside "TrekkInn" — a safe control wrapped in a lying
                  display. An operator who copies that number and acts on it elsewhere is a real
                  path, so the numbers are replaced outright rather than annotated. */}
              {p && !writable && (
                <div
                  className="rounded border border-amber-500 p-3 text-sm text-amber-700"
                  data-testid={`${e}-preview-stale`}
                >
                  The scope changed since this preview ran. Re-run the preview — the previous count
                  described a different selection.
                </div>
              )}

              {p && writable && (
                <div className="rounded border p-3 text-sm" data-testid={`${e}-preview-result`}>
                  <div className="mb-2 font-medium">
                    Would enqueue <Badge variant="secondary">{p.count}</Badge>
                  </div>
                  <ul className="max-h-40 list-disc overflow-auto pl-5 text-muted-foreground">
                    {p.items.slice(0, 60).map((it, i) => (
                      <li key={i}>{it}</li>
                    ))}
                  </ul>
                  {p.items.length > 60 && (
                    <div className="mt-1 text-xs">…and {p.items.length - 60} more</div>
                  )}
                </div>
              )}

              {/* ⇒ The label NAMES THE NUMBER. "Run" tells the operator nothing about blast radius. */}
              <Button
                variant="destructive"
                disabled={!writable || busy !== null}
                onClick={() => runWrite(e)}
                data-testid={`${e}-write`}
              >
                {writable
                  ? `2. Enqueue ${p?.count} rewrite${p?.count === 1 ? '' : 's'}`
                  : '2. Enqueue — run a preview first'}
              </Button>

              {r && (
                <div className="rounded border p-3 text-sm" data-testid={`${e}-result`}>
                  {/* ⇒ SKIPPED AND FAILED ARE SHOWN, ALWAYS. A skip that is invisible is how
                      "enqueued 1 of 2" gets read as success. */}
                  Enqueued <strong>{r.enqueued}</strong> · skipped <strong>{r.skipped}</strong> ·
                  failed <strong>{r.failed}</strong>
                  {r.failed > 0 && (
                    <div className="mt-1 text-destructive">
                      {r.failures?.[0]?.error || 'see server logs'}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {errMsg && (
            <p className="text-sm text-destructive" data-testid={`${e}-error`}>
              {errMsg}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="persona-rewrite-enqueue">
      {panel(
        'canonical',
        'Rewrite canonical persona prose',
        'Rewrites canonical_personas descriptions into name-free, gender-neutral prose. ' +
          'canonical_description wins the prompt priority chain, so these are what most generated ' +
          'pages actually read. Runs all rows — there is no selector to be wrong.',
        true,
        '',
      )}
      {panel(
        'category',
        'Rewrite category persona prose (this site)',
        selectedSite
          ? `Rewrites the personas on category rows for ${selectedSite.name}.`
          : 'Rewrites the personas on category rows for the selected site.',
        Boolean(siteId),
        'Select a site in the header first. This action is deliberately site-scoped — there is no ' +
          'corpus-wide option on this screen.',
      )}
    </div>
  );
}

export default PersonaRewriteEnqueue;
